import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';

export type StatusBadgeVariant = 'success' | 'danger' | 'warning' | 'info' | 'neutral' | 'processing';
export type StatusBadgeSize = 'sm' | 'md';

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Badge variant determines color scheme */
  variant?: StatusBadgeVariant;
  /** Badge size */
  size?: StatusBadgeSize;
  /** Show dot indicator before text */
  showDot?: boolean;
  /** Children content (text label) */
  children?: ReactNode;
}

/**
 * StatusBadge component with CGIC brand styling
 *
 * A pill-shaped badge for displaying status information.
 * Supports multiple variants (success, danger, warning, info, neutral, processing)
 * and sizes (sm, md). Includes optional dot indicator with pulse animation for processing state.
 */
export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(
  (
    {
      variant = 'neutral',
      size = 'md',
      showDot = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    // Base classes for all badges
    const baseClasses = [
      'inline-flex items-center gap-1.5',
      'font-body font-medium',
      'rounded-full',
      'whitespace-nowrap',
    ];

    // Size variants
    const sizeClasses: Record<StatusBadgeSize, string> = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
    };

    // Variant styles with light and dark mode support
    const variantClasses: Record<StatusBadgeVariant, string> = {
      success: [
        'bg-success-100 text-success-700',
        'dark:bg-success-900/30 dark:text-success-400',
      ].join(' '),
      danger: [
        'bg-danger-100 text-danger-700',
        'dark:bg-danger-900/30 dark:text-danger-400',
      ].join(' '),
      warning: [
        'bg-warning-100 text-warning-700',
        'dark:bg-warning-900/30 dark:text-warning-400',
      ].join(' '),
      info: [
        'bg-primary-100 text-primary-700',
        'dark:bg-primary-900/30 dark:text-primary-400',
      ].join(' '),
      neutral: [
        'bg-neutral-100 text-neutral-600',
        'dark:bg-neutral-800 dark:text-neutral-400',
      ].join(' '),
      processing: [
        'bg-primary-100 text-primary-700',
        'dark:bg-primary-900/30 dark:text-primary-400',
      ].join(' '),
    };

    // Dot size classes
    const dotSizeClasses: Record<StatusBadgeSize, string> = {
      sm: 'w-1.5 h-1.5',
      md: 'w-2 h-2',
    };

    // Dot color classes by variant
    const dotColorClasses: Record<StatusBadgeVariant, string> = {
      success: 'bg-success-500 dark:bg-success-400',
      danger: 'bg-danger-500 dark:bg-danger-400',
      warning: 'bg-warning-500 dark:bg-warning-400',
      info: 'bg-primary-500 dark:bg-primary-400',
      neutral: 'bg-neutral-500 dark:bg-neutral-400',
      processing: 'bg-primary-500 dark:bg-primary-400',
    };

    const combinedClassName = [
      ...baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const dotClassName = [
      'rounded-full flex-shrink-0',
      dotSizeClasses[size],
      dotColorClasses[variant],
      variant === 'processing' ? 'animate-pulse' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <span ref={ref} className={combinedClassName} {...props}>
        {showDot && <span className={dotClassName} aria-hidden="true" />}
        {children}
      </span>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';

export default StatusBadge;
